// DetailContact.jsx
import React, { useEffect, useState } from "react";
import { Button, Card, Container, Row, Col, Badge } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  fetchContactById,
  updateContactStatus,
} from "../../../Redux/contactSlice";

function DetailContact() {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const dispatch = useDispatch();
  const navigate = useHistory();
  const { id } = useParams();

  const notify = (type, msg) => {
    if (type === 1)
      toast.success(
        <strong>
          <i className="fas fa-check-circle"></i> {msg}
        </strong>,
      );
    else
      toast.error(
        <strong>
          <i className="fas fa-exclamation-circle"></i> {msg}
        </strong>,
      );
  };

  useEffect(() => {
    getContactDetail();
  }, [id]);

  const getContactDetail = async () => {
    setLoading(true);
    try {
      const response = await dispatch(fetchContactById(id));
      setContact(response.payload);
      // Auto-mark as read when viewing details
      if (response.payload && response.payload.status !== "read") {
        await markAsRead(response.payload.id);
      }
    } catch (error) {
      notify(2, "Failed to fetch contact details");
      navigate.push("/contact/list");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (contactId) => {
    try {
      await dispatch(updateContactStatus({ id: contactId, status: "read" }));
      if (contact) {
        setContact({ ...contact, status: "read" });
      }
    } catch (error) {
      console.error("Failed to mark as read", error);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await dispatch(
        updateContactStatus({ id: contact.id, status: newStatus }),
      );
      setContact({ ...contact, status: newStatus });
      notify(1, `Message marked as ${newStatus}`);
    } catch (error) {
      notify(2, "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleReply = () => {
    window.location.href = `mailto:${contact.email}?subject=Re: ${contact.subject}`;
  };

  const goBack = () => {
    navigate.goBack();
  };

  if (loading) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <i className="fas fa-spinner fa-spin fa-3x"></i>
          <p>Loading contact details...</p>
        </div>
      </Container>
    );
  }

  if (!contact) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <i className="fas fa-exclamation-triangle fa-3x text-warning"></i>
          <p>Contact message not found</p>
          <Button variant="primary" onClick={goBack}>
            Go Back
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <>
      <Container fluid>
        <ToastContainer />
        <Row>
          <Col md="12">
            <Button
              className="btn-wd btn-outline mr-2 mb-3"
              type="button"
              variant="secondary"
              onClick={goBack}
            >
              <span className="btn-label">
                <i className="fas fa-arrow-left"></i>
              </span>
              Back to List
            </Button>

            <Button
              className="btn-wd btn-outline mr-2 mb-3"
              type="button"
              variant="info"
              onClick={handleReply}
            >
              <span className="btn-label">
                <i className="fas fa-reply"></i>
              </span>
              Reply by Email
            </Button>

            {contact.status !== "read" && (
              <Button
                className="btn-wd btn-outline mb-3"
                type="button"
                variant="success"
                onClick={() => handleStatusUpdate("read")}
                disabled={updating}
              >
                <span className="btn-label">
                  <i className="fas fa-check"></i>
                </span>
                Mark as Read
              </Button>
            )}

            <Card>
              <Card.Header>
                <Card.Title as="h4">
                  Contact Message Details
                  <Badge
                    bg={contact.status === "read" ? "success" : "warning"}
                    className="ms-2"
                  >
                    {contact.status || "unread"}
                  </Badge>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {/* Sender Information */}
                <Row className="mb-4">
                  <Col md="12">
                    <h5 className="text-primary">Sender Information</h5>
                    <hr />
                  </Col>
                  <Col md="6">
                    <div className="info-item">
                      <label className="font-weight-bold">Full Name:</label>
                      <p>
                        {contact.firstName} {contact.lastName}
                      </p>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="info-item">
                      <label className="font-weight-bold">Email:</label>
                      <p>
                        <a href={`mailto:${contact.email}`}>{contact.email}</a>
                      </p>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="info-item">
                      <label className="font-weight-bold">Phone:</label>
                      <p>
                        <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                      </p>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="info-item">
                      <label className="font-weight-bold">Submitted On:</label>
                      <p>{new Date(contact.createdAt).toLocaleString()}</p>
                    </div>
                  </Col>
                </Row>

                {/* Message Information */}
                <Row className="mb-4">
                  <Col md="12">
                    <h5 className="text-primary">Message Information</h5>
                    <hr />
                  </Col>
                  <Col md="12">
                    <div className="info-item">
                      <label className="font-weight-bold">Subject:</label>
                      <p className="h6">{contact.subject}</p>
                    </div>
                  </Col>
                  <Col md="12">
                    <div className="info-item">
                      <label className="font-weight-bold">Message:</label>
                      <div className="message-box p-3 bg-light rounded">
                        <p style={{ whiteSpace: "pre-wrap" }}>
                          {contact.message}
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Additional Information if available */}
                {contact.company && (
                  <Row className="mb-4">
                    <Col md="12">
                      <h5 className="text-primary">Additional Information</h5>
                      <hr />
                    </Col>
                    <Col md="6">
                      <div className="info-item">
                        <label className="font-weight-bold">Company:</label>
                        <p>{contact.company}</p>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Action Buttons */}
                <Row className="mt-4">
                  <Col md="12" className="text-right">
                    <Button
                      variant="danger"
                      onClick={() => {
                        if (
                          window.confirm(
                            "Are you sure you want to delete this message?",
                          )
                        ) {
                          // Handle delete
                        }
                      }}
                    >
                      <i className="fas fa-trash"></i> Delete
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .info-item label {
          font-weight: 600;
          color: #666;
          margin-bottom: 5px;
          display: block;
        }
        .info-item p {
          margin-bottom: 15px;
          font-size: 14px;
        }
        .message-box {
          background-color: #f8f9fa;
          border-left: 4px solid #007bff;
          margin-top: 10px;
        }
        .opacity-50 {
          opacity: 0.5;
        }
      `}</style>
    </>
  );
}

export default DetailContact;
